/**
 * Coût de revient d'un kit, par période : lecture et saisie.
 *
 * Chaque écriture renvoie la liste entière, posée dans le cache ; les
 * statistiques de ventes (COGS, marges) sont invalidées : elles dépendent du
 * coût en vigueur à la date de chaque commande.
 */

import type { KitCostPeriod, KitCostPeriodInput } from '@shared/types/settings';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiRequestError, createKitCost, deleteKitCost, fetchKitCosts, updateKitCost } from '../lib/api';
import { useAuth } from './useAuth';

const KIT_COSTS_KEY = ['kit-cost-periods'] as const;

export function useKitCosts() {
  const { token, signOut } = useAuth();
  return useQuery<KitCostPeriod[], Error>({
    queryKey: KIT_COSTS_KEY,
    queryFn: async ({ signal }) => {
      if (!token) throw new ApiRequestError(401, 'Session expiree');
      try {
        return (await fetchKitCosts(token, signal)).periods;
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) signOut();
        throw error;
      }
    },
    enabled: Boolean(token),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) =>
      !(error instanceof ApiRequestError && error.status >= 400 && error.status < 500) && failureCount < 2,
  });
}

type KitCostWrite =
  | { kind: 'create'; input: KitCostPeriodInput }
  | { kind: 'update'; id: number; input: KitCostPeriodInput }
  | { kind: 'delete'; id: number };

export function useWriteKitCost() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  return useMutation<KitCostPeriod[], Error, KitCostWrite>({
    mutationFn: async (write) => {
      if (!token) throw new ApiRequestError(401, 'Session expiree');
      const response =
        write.kind === 'create'
          ? await createKitCost(write.input, token)
          : write.kind === 'update'
            ? await updateKitCost(write.id, write.input, token)
            : await deleteKitCost(write.id, token);
      return response.periods;
    },
    onSuccess: async (periods) => {
      queryClient.setQueryData(KIT_COSTS_KEY, periods);
      // Le COGS de chaque page de ventes dépend du coût en vigueur : tout se recalcule.
      await queryClient.invalidateQueries({
        predicate: (query) => /^(sales|btob)/.test(String(query.queryKey[0])),
      });
    },
  });
}
